import moment from 'moment';
import {isAxiosError} from 'axios';
import {VanillaException} from '@/exceptions/VanillaException';
import {TimetableItem, TimetableLessonMoveInfo} from '@/interfaces/timetable/TimetableItem';
import {TimetableItemTypes} from '@/enums/timetable/TimetableItemTypes';
import {TimetableCancellationReason} from '@/enums/scheduler/TimetableCancellationReason.enum';
import {DATE_TIME_FORMAT, DAY_FORMAT} from '@/helpers/timetable2/types';

/**
 * "Stunde einmalig verschieben" (pure helpers, no API).
 *
 * Two mechanisms (decided by `useSegmentUpdate`):
 * - target in the SAME ISO week of a weekly course: the existing single scope course update (UpdateCourseTimeDialog
 *   "Nur diese Stunde": startingFrom/until = the week, dayOfWeek + timeRange of the new slot).
 * - otherwise (other week, or weekInterval > 1): a LessonMove in course-manager (POST lesson-moves). The scheduler hides
 *   the original and emits one event at the new date ('moved'); the calendar also gets a ghost at the original date
 *   ('origin', isCanceled + cancellationReason MOVED).
 * The duration of the lesson always stays the same (the backend refuses other durations).
 */

export type MoveRole = TimetableLessonMoveInfo['role'];

type DateInput = Date | string | moment.Moment;

const EVENT_FORMATS = [DATE_TIME_FORMAT, DAY_FORMAT];
const TIME_PATTERN = /^([01]?\d|2[0-3]):([0-5]\d)$/;

/** Event strings ('YYYY-MM-DD HH:mm') strictly, ISO strings / Dates as usual */
export function toMoment(value: DateInput): moment.Moment {
    if (typeof value === 'string') {
        const parsed = moment(value, EVENT_FORMATS, true);
        return parsed.isValid() ? parsed : moment(value);
    }
    return moment(value);
}

/** ISO week key 'GGGG-WW' (local time = Europe/Berlin for the users; same format as LessonMove.originalWeekKey) */
export function isoWeekKey(value: DateInput): string {
    return toMoment(value).format('GGGG-WW');
}

export function moveInfoOf(item: Pick<TimetableItem<unknown>, 'lessonMove'> | undefined): TimetableLessonMoveInfo | undefined {
    return item?.lessonMove && (item.lessonMove.role === 'moved' || item.lessonMove.role === 'origin') ? item.lessonMove : undefined;
}

export function moveRoleOf(item: Pick<TimetableItem<unknown>, 'lessonMove'> | undefined): MoveRole | undefined {
    return moveInfoOf(item)?.role;
}

/** 'YYYY-MM-DD HH:mm' of a date of the move info */
export function eventText(value: DateInput): string {
    return toMoment(value).format(DATE_TIME_FORMAT);
}

/** Badge of the moved lesson: "Verschoben vom 12.10." */
export function movedFromText(info: Pick<TimetableLessonMoveInfo, 'originalDate'>): string {
    return `Verschoben vom ${toMoment(info.originalDate).format('DD.MM.')}`;
}

/** Text of the ghost at the original date: "Verschoben auf 19.10., 15:00" */
export function movedToText(info: Pick<TimetableLessonMoveInfo, 'newStart'>): string {
    const start = toMoment(info.newStart);
    return `Verschoben auf ${start.format('DD.MM.')}, ${start.format('HH:mm')}`;
}

/** Status text of a lesson (badge for 'moved', ghost text for 'origin') */
export function moveBadgeText(info: TimetableLessonMoveInfo | undefined): string {
    if (!info) {
        return '';
    }
    return info.role === 'moved' ? movedFromText(info) : movedToText(info);
}

/** Where "Zum ursprünglichen Termin" / "Zum neuen Termin" leads: day of the calendar and start of the other occurrence */
export function counterpartOf(info: TimetableLessonMoveInfo): { day: string; start: string; text: string } {
    const target = info.role === 'moved' ? info.originalDate : info.newStart;
    const start = toMoment(target);
    return {
        day: start.format(DAY_FORMAT),
        start: start.format(DATE_TIME_FORMAT),
        text: info.role === 'moved' ? 'Zum ursprünglichen Termin' : 'Zum neuen Termin',
    };
}

/** Same week of a weekly course: existing single scope course update instead of a LessonMove */
export function useSegmentUpdate(originalStart: DateInput, targetStart: DateInput, weekInterval?: number): boolean {
    return (weekInterval || 1) === 1 && isoWeekKey(originalStart) === isoWeekKey(targetStart);
}

/** Wall clock minutes of a lesson (DST safe) */
export function durationMinutes(start: DateInput, end: DateInput): number {
    const s = toMoment(start);
    const e = toMoment(end);
    const days = e.clone().startOf('day').diff(s.clone().startOf('day'), 'days');
    return days * 24 * 60 + (e.hours() * 60 + e.minutes()) - (s.hours() * 60 + s.minutes());
}

export interface MoveTarget {
    /** 'YYYY-MM-DD' */
    day: string;
    /** 'HH:mm' */
    startTime: string;
}

/** German error text of the target form, undefined when valid */
export function moveTargetError(target: MoveTarget, duration: number): string | undefined {
    if (!target.day || !moment(target.day, DAY_FORMAT, true).isValid()) {
        return 'Bitte ein Datum wählen.';
    }
    const match = TIME_PATTERN.exec((target.startTime || '').trim());
    if (!match) {
        return 'Bitte die Uhrzeit im Format HH:MM eingeben.';
    }
    if (duration <= 0) {
        return 'Die Dauer der Stunde ist unbekannt.';
    }
    if (Number(match[1]) * 60 + Number(match[2]) + duration > 24 * 60) {
        return 'Die Stunde muss am selben Tag enden.';
    }
    return undefined;
}

/** New start/end ('YYYY-MM-DD HH:mm') with the kept duration; undefined when the form is invalid */
export function moveTargetTimes(target: MoveTarget, duration: number): { start: string; end: string } | undefined {
    if (moveTargetError(target, duration)) {
        return undefined;
    }
    const start = moment(`${target.day} ${target.startTime.trim().padStart(5, '0')}`, DATE_TIME_FORMAT, true);
    return {start: start.format(DATE_TIME_FORMAT), end: start.clone().add(duration, 'minutes').format(DATE_TIME_FORMAT)};
}

/** "Mo, 12.10.2026, 15:00–15:45" */
export function slotText(start: DateInput, end: DateInput): string {
    const s = toMoment(start);
    return `${s.format('dd, DD.MM.YYYY')}, ${s.format('HH:mm')}–${toMoment(end).format('HH:mm')}`;
}

/** Reference box of the dialog: "Ursprünglich: Mo, 12.10.2026, 15:00–15:45, Raum 1" */
export function originalReferenceText(start: DateInput, end: DateInput, roomName?: string): string {
    return `Ursprünglich: ${[slotText(start, end), roomName].filter(Boolean).join(', ')}`;
}

export const MOVE_ORIGIN_TEXT = 'Diese Stunde wurde einmalig verschoben. Änderungen und Protokolle gehören zum neuen Termin.';
export const MOVED_LESSON_TEXT = 'Diese Stunde ist einmalig verschoben. Lehrkraft, Raum und Zeit bitte über „Verschiebung ändern“ anpassen.';
export const MOVE_PAST_TEXT = 'Vergangene Stunden können nicht verschoben werden.';
export const MOVE_DRAFT_TEXT = 'Entwürfe können nicht verschoben werden.';
export const MOVE_HOLIDAY_TEXT = 'Die Stunde fällt wegen Ferien aus und kann nicht verschoben werden.';
export const MOVE_INQUIRY_TEXT = 'Für diese Stunde gibt es eine Vertretungsanfrage bzw. Absage. Sie kann deshalb nicht verschoben werden.';

export interface MoveGuardInput {
    role?: MoveRole;
    isDraft: boolean;
    /** Holiday cancellation of the scheduler (not the MOVED ghost) */
    isHoliday: boolean;
    hasInquiry: boolean;
    /** Lesson start is not in the future */
    isPast: boolean;
    /** Custom key MOVE_PAST_LESSONS */
    canMovePast: boolean;
    /** Course logs exist for this lesson */
    logged: boolean;
}

/**
 * Reason why "Stunde einmalig verschieben" (or changing an existing move) is not possible, undefined when allowed.
 * Mirrors the backend rules that can be checked in the browser (billing and inquiries are checked by course-manager).
 */
export function moveBlockedReason(f: MoveGuardInput, loggedText: string): string | undefined {
    if (f.role === 'origin') {
        return MOVE_ORIGIN_TEXT;
    }
    if (f.isDraft) {
        return MOVE_DRAFT_TEXT;
    }
    if (f.isHoliday) {
        return MOVE_HOLIDAY_TEXT;
    }
    if (f.logged) {
        return loggedText;
    }
    if (f.hasInquiry && f.role !== 'moved') {
        return MOVE_INQUIRY_TEXT;
    }
    if (f.isPast && !f.canMovePast) {
        return MOVE_PAST_TEXT;
    }
    return undefined;
}

/** Past target dates are only allowed with MOVE_PAST_LESSONS */
export function targetPastError(start: DateInput | undefined, now: DateInput, canMovePast: boolean): string | undefined {
    if (!start || canMovePast) {
        return undefined;
    }
    return toMoment(start).isAfter(toMoment(now)) ? undefined : 'Der neue Termin liegt in der Vergangenheit.';
}

/** German message of a 409/422 of course-manager (lesson moves) or the recorder ("Stunde wurde auf … verschoben") */
export function backendMessage(error: unknown, statuses: number[] = [409, 422]): string | undefined {
    if (!isAxiosError(error) || !statuses.includes(error.response?.status || 0)) {
        return undefined;
    }
    const message = (error.response?.data as { message?: unknown } | undefined)?.message;
    if (typeof message === 'string' && message) {
        return message;
    }
    if (Array.isArray(message) && message.length) {
        return message.filter((m) => typeof m === 'string').join(', ') || undefined;
    }
    return undefined;
}

/**
 * Message of the recorder when a log is written for the moved-away original of a lesson
 * (RecorderExceptions.LessonMovedException or a plain 422 "Stunde wurde auf … verschoben")
 */
export function lessonMovedLogMessage(error: unknown): string | undefined {
    if (error instanceof VanillaException) {
        return /verschoben/i.test(error.message) ? error.message : undefined;
    }
    const message = backendMessage(error, [422]);
    return message && /verschoben/i.test(message) ? message : undefined;
}

export const SAME_DAY_LESSON_TEXT = 'Kurs hat an diesem Tag bereits eine Stunde';

interface SameDayInput {
    customId: string;
    /** Edit: the moved lesson itself is no conflict */
    ownMoveId?: string;
    /** Same-week course update ("Nur diese Stunde"): the lesson itself (segment + original start) is no conflict */
    ownLesson?: { segmentId: string; start: string };
}

function idText(value: unknown): string {
    const v = value as { _id?: unknown } | undefined;
    return v && typeof v === 'object' && v._id ? String(v._id) : value ? String(value) : '';
}

/**
 * Another lesson of the same course on the target day (same rule as course-manager assertTargetFree, which answers 409):
 * COURSE events of the customId except moved-away originals and the own move.
 */
export function sameDayLesson(events: Array<TimetableItem<any>>, input: SameDayInput): TimetableItem<any> | undefined {
    return (events || []).find((ev) => ev.type === TimetableItemTypes.COURSE && ev.data?.customId === input.customId
        && !(ev.isCanceled && ev.cancellationReason === TimetableCancellationReason.MOVED)
        && !(input.ownMoveId && idText(ev.lessonMove?._id) === input.ownMoveId)
        && !(input.ownLesson && idText(ev.data?._id || ev._id) === input.ownLesson.segmentId && eventText(ev.start) === input.ownLesson.start));
}

export function sameDayLessonText(event: Pick<TimetableItem<unknown>, 'start' | 'end'>): string {
    return `${SAME_DAY_LESSON_TEXT} (${slotText(event.start, event.end)}). Pro Tag ist nur eine Stunde des Kurses möglich – bitte einen anderen Tag wählen.`;
}
